import { useEffect, useRef, useState } from "react";
import TextInput from "../../../base/TextInput/TextInput";
import { hasActiveFilters, useSearchStore } from "../../../../store/searchStore";
import { fetchSuggestions } from "../../../../services/api";
import { ReactComponent as MagnifyingGlassIcon } from "../../../../assets/icons/ui/magnifying-glass.svg";
import styles from "./AiSearchBox.module.scss";

const EXAMPLE_POOL = [
  "3 bed under $800k",
  "Cabins with Teton views",
  "Condos near Driggs",
  "New listings this month",
  "Land over 5 acres",
  "Riverfront property",
  "Horse property near Victor",
  "New construction in Driggs",
];

/** Example chips shown at once; kept low so they stay on a single row. */
const EXAMPLES_SHOWN = 3;

/** Debounce window before firing an autocomplete request, in ms. */
const SUGGEST_DEBOUNCE_MS = 180;
/** Minimum trimmed length before we ask the server for suggestions. */
const SUGGEST_MIN_CHARS = 2;

/** One property hit from the Autocomplete endpoint (camelCase, nullable). */
interface SuggestionProperty {
  id: string;
  address: string;
  city: string;
  state: string;
  price: number | null;
  image: string;
  propertyType: string;
  unparsedAddress: string;
  beds: number | null;
  baths: number | null;
  url: string;
}

/** Grouped Autocomplete payload returned by fetchSuggestions. */
interface Suggestions {
  properties: SuggestionProperty[];
  cities: string[];
  subdivisions: string[];
  /** Set when nothing matched literally and these are near-miss matches. */
  fuzzy?: boolean;
}

/**
 * Flat list of the keyboard-navigable rows the dropdown renders. The index
 * into this array drives ArrowUp/ArrowDown highlighting and Enter activation,
 * mirroring the activeItems array in the no-JS SmartSearch.cshtml block.
 */
type Row =
  | { kind: "property"; url: string }
  | { kind: "city"; value: string }
  | { kind: "subdivision"; value: string }
  | { kind: "ai"; value: string };

function formatPrice(n: number | null): string {
  if (n == null) return "";
  try {
    return "$" + Math.round(n).toLocaleString();
  } catch {
    return "$" + n;
  }
}

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
    <path
      d="M18 6 6 18M6 6l12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Natural-language property search. Replaces the old exact-match location
 * box. Submitting parses the query server-side, applies the resulting
 * structured filters, and runs the normal search (so pagination / map
 * bounds / sort all work). The interpreted summary is shown below.
 *
 * While the box has query text we also show a live autocomplete dropdown
 * (properties / cities / subdivisions) fed by the ListingsApi/Autocomplete
 * endpoint, plus a final row that runs the AI parse on the raw text.
 *
 * Place and keyword filters have no control of their own in the form, so
 * once the AI summary is gone (a manual filter edit exits AI mode) they are
 * shown here as removable chips. Without them a search stays quietly scoped
 * to "Driggs" or "horse property" with nothing on screen saying so.
 *
 * The x in the right end of the box clears in two steps. Text typed but not
 * yet searched goes first, as in any search box. After that it clears every
 * filter, the AI query and the map-area limit, in one search.
 */
export default function AiSearchBox() {
  const aiQuery = useSearchStore((s) => s.aiQuery);
  const aiSummary = useSearchStore((s) => s.aiSummary);
  const aiError = useSearchStore((s) => s.aiError);
  const runAiQuery = useSearchStore((s) => s.runAiQuery);
  const clearAllFilters = useSearchStore((s) => s.clearAllFilters);
  const setAiError = useSearchStore((s) => s.setAiError);
  const applyLocation = useSearchStore((s) => s.applyLocation);
  const applySubdivision = useSearchStore((s) => s.applySubdivision);
  const setField = useSearchStore((s) => s.setField);
  const location = useSearchStore((s) => s.formData.location);
  const subdivision = useSearchStore((s) => s.formData.subdivision);
  const address = useSearchStore((s) => s.formData.address);
  const keywords = useSearchStore((s) => s.formData.keywords);
  const maxDaysOnMarket = useSearchStore((s) => s.formData.maxDaysOnMarket);
  const filtersActive = useSearchStore((s) => hasActiveFilters(s.formData));
  const mapAreaActive = useSearchStore((s) => s.mapBounds !== null);

  const [text, setText] = useState(aiQuery);
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Randomized once per page load, deliberately NOT rotated on a timer.
  const [exampleOffset] = useState(() => Math.floor(Math.random() * EXAMPLE_POOL.length));

  const blurTimer = useRef<number | null>(null);
  const debounceTimer = useRef<number | null>(null);
  const controller = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reflect external changes: arrived via ?q=, or cleared by a manual filter edit.
  useEffect(() => {
    setText(aiQuery);
  }, [aiQuery]);

  // Cancel in-flight fetches and pending timers on unmount.
  useEffect(() => {
    return () => {
      if (blurTimer.current) window.clearTimeout(blurTimer.current);
      if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
      if (controller.current) controller.current.abort();
    };
  }, []);

  // Debounced autocomplete: abort the previous request each keystroke, then
  // ask the server once the box settles. Short input clears the dropdown.
  useEffect(() => {
    const q = text.trim();

    if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
    if (controller.current) controller.current.abort();

    // Skip when the box isn't focused (text set programmatically by an
    // autocomplete pick or a ?q= restore) or too short: the result would never
    // be shown, so there is no point spending the request.
    if (!focused || q.length < SUGGEST_MIN_CHARS) {
      setSuggestions(null);
      setActiveIndex(-1);
      return;
    }

    debounceTimer.current = window.setTimeout(() => {
      const ac = new AbortController();
      controller.current = ac;
      fetchSuggestions(q, ac.signal)
        .then((data) => {
          // Same runtime shape; the api module owns its own type name.
          setSuggestions(data as Suggestions);
          setActiveIndex(-1);
        })
        .catch((err) => {
          if (err && err.name === "AbortError") return;
          setSuggestions(null);
          setActiveIndex(-1);
        });
    }, SUGGEST_DEBOUNCE_MS);
  }, [text, focused]);

  const close = () => {
    setFocused(false);
    setActiveIndex(-1);
  };

  const submit = (q: string) => {
    const v = q.trim();
    if (!v) return;
    close();
    runAiQuery(v);
  };

  const selectProperty = (url: string) => {
    if (!url) return;
    window.location.href = url;
  };

  const selectCity = (city: string) => {
    close();
    // Emptied explicitly rather than left to the aiQuery effect: the store
    // already holds "", so that effect would not re-fire and the picked city
    // would sit in the box blocking the next one from being typed.
    setText("");
    setSuggestions(null);
    applyLocation(city);
  };

  const selectSubdivision = (name: string) => {
    close();
    applySubdivision(name);
  };

  const trimmed = text.trim();
  const hasQuery = trimmed.length >= SUGGEST_MIN_CHARS;
  const properties = suggestions?.properties ?? [];
  const cities = suggestions?.cities ?? [];
  const subdivisions = suggestions?.subdivisions ?? [];
  const hasResults = properties.length > 0 || cities.length > 0 || subdivisions.length > 0;
  // Nothing matched the text as typed; these are the closest things found.
  const isFuzzy = suggestions?.fuzzy === true;

  // City first, then individual listings, then subdivisions: a town is the
  // broadest thing the typed text is likely to mean, so it goes at the top.
  const cityStart = 0;
  const propertyStart = cities.length;
  const subdivisionStart = cities.length + properties.length;

  // The dropdown shows suggestions whenever there's query text (with results
  // or a final AI row); the example chips only show on an empty, focused box.
  const showSuggestions = focused && hasQuery && !aiError;
  const showChips = focused && !hasQuery && !aiSummary && !aiError;

  const examples = Array.from(
    { length: EXAMPLES_SHOWN },
    (_, i) => EXAMPLE_POOL[(exampleOffset + i) % EXAMPLE_POOL.length]
  );

  // Active filters that have no dedicated control. Hidden while the AI
  // summary is up (it already spells them out, and the x in the box clears it).
  const activeFilters: { key: string; label: string; clear: () => void }[] = [];
  if (!aiSummary) {
    // One chip per city, each clearable on its own, so dropping Victor from a
    // Driggs + Victor search does not throw the whole location filter away.
    location.forEach((city) =>
      activeFilters.push({
        key: `city:${city}`,
        label: city,
        clear: () =>
          setField(
            "location",
            location.filter((c) => c !== city)
          ),
      })
    );
    if (subdivision)
      activeFilters.push({
        key: "subdivision",
        label: subdivision,
        clear: () => setField("subdivision", ""),
      });
    if (address)
      activeFilters.push({ key: "address", label: address, clear: () => setField("address", "") });
    keywords.forEach((k) =>
      activeFilters.push({
        key: `kw:${k}`,
        label: `“${k}”`,
        clear: () =>
          setField(
            "keywords",
            keywords.filter((x) => x !== k)
          ),
      })
    );
    if (maxDaysOnMarket && maxDaysOnMarket > 0)
      activeFilters.push({
        key: "recency",
        label: maxDaysOnMarket <= 7 ? "Listed this week" : `Listed in last ${maxDaysOnMarket} days`,
        clear: () => setField("maxDaysOnMarket", undefined),
      });
  }

  // Build the flat row list in the same order the dropdown renders, so the
  // highlight index lines up with what the user sees.
  const rows: Row[] = [];
  cities.forEach((c) => rows.push({ kind: "city", value: c }));
  properties.forEach((p) => rows.push({ kind: "property", url: p.url }));
  subdivisions.forEach((s) => rows.push({ kind: "subdivision", value: s }));
  if (hasQuery) rows.push({ kind: "ai", value: trimmed });

  const activateRow = (row: Row) => {
    switch (row.kind) {
      case "property":
        selectProperty(row.url);
        break;
      case "city":
        selectCity(row.value);
        break;
      case "subdivision":
        selectSubdivision(row.value);
        break;
      case "ai":
        submit(row.value);
        break;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && rows.length) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % rows.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + rows.length) % rows.length);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === "Enter" && activeIndex >= 0 && rows[activeIndex]) {
        // A highlighted row wins; otherwise fall through to the AI submit.
        e.preventDefault();
        activateRow(rows[activeIndex]);
        return;
      }
    }

    if (e.key === "Enter") {
      e.preventDefault();
      submit(text);
    }
  };

  const open = showSuggestions || showChips;

  // What the x clears. Text typed but not searched goes first, like any search
  // box, so erasing a half-typed word never wipes the filters below. After
  // that it clears whatever the store holds: a filter, the AI query or its
  // message, or a map-area limit.
  const unsentText = trimmed.length > 0 && trimmed !== aiQuery.trim();
  const storeHasSomething = filtersActive || mapAreaActive || !!aiQuery || !!aiSummary || !!aiError;
  const canClear = unsentText || storeHasSomething;
  const clearLabel = unsentText ? "Clear search text" : "Clear all filters";

  const clearAll = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (blurTimer.current) window.clearTimeout(blurTimer.current);
    // Emptied explicitly: when aiQuery is already "" the effect that mirrors
    // it would not re-fire, and the text would stay in the box.
    setText("");
    setSuggestions(null);
    setActiveIndex(-1);
    // Focus goes to the box only when it was there already or the x was
    // pressed from the keyboard (detail 0). A click never moves focus (its
    // mousedown is prevented), and focusing on a tap would open a phone's
    // keyboard. After a key press the button may unmount, and focus would
    // otherwise drop to <body>.
    if (e.detail === 0 || document.activeElement === inputRef.current) {
      inputRef.current?.focus();
      setFocused(true);
    }
    if (!unsentText && storeHasSomething) clearAllFilters();
  };

  return (
    <div className={styles.aiSearch}>
      <TextInput
        ref={inputRef}
        placeholder='Try "3 bed cabin under $800k"'
        icon={<MagnifyingGlassIcon className={styles.icon} />}
        endAdornment={
          canClear ? (
            <button
              type="button"
              className={styles.clearAll}
              // Keep focus in the box: a blur would start the dropdown's close timer.
              onMouseDown={(e) => e.preventDefault()}
              onClick={clearAll}
              aria-label={clearLabel}
              title={clearLabel}
            >
              <CloseIcon />
            </button>
          ) : undefined
        }
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          // Typing counts as focus. Picking a suggestion closes the dropdown
          // without the input ever losing DOM focus (the row's mousedown is
          // prevented), so onFocus will not fire again and typing the next
          // city would otherwise show nothing.
          setFocused(true);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          blurTimer.current = window.setTimeout(close, 150);
        }}
        onKeyDown={handleKeyDown}
        aria-label="AI property search"
        aria-expanded={open}
        aria-autocomplete="list"
      />

      {aiError && (
        <div className={styles.error} role="alert">
          <span className={styles.summaryText}>{aiError}</span>
          <button
            type="button"
            className={styles.summaryClear}
            onClick={() => setAiError("")}
            aria-label="Dismiss"
            title="Dismiss"
          >
            <CloseIcon />
          </button>
        </div>
      )}

      {showChips && (
        <div className={styles.chips}>
          {examples.map((ex) => (
            <button
              key={ex}
              type="button"
              className={styles.chip}
              onMouseDown={(e) => {
                e.preventDefault();
                if (blurTimer.current) window.clearTimeout(blurTimer.current);
                setText(ex);
                submit(ex);
              }}
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {showSuggestions && (hasResults || hasQuery) && (
        <div className={styles.suggestions} role="listbox">
          {isFuzzy && hasResults && <div className={styles.groupLabel}>Did you mean</div>}

          {cities.length > 0 && (
            <div className={styles.group} role="group" aria-label="Cities">
              <div className={styles.groupLabel}>Cities</div>
              {cities.map((c, i) => {
                const rowIndex = cityStart + i;
                return (
                  <button
                    key={c}
                    type="button"
                    role="option"
                    aria-selected={activeIndex === rowIndex}
                    className={`${styles.row} ${styles.tagRow} ${
                      activeIndex === rowIndex ? styles.rowActive : ""
                    }`}
                    onMouseEnter={() => setActiveIndex(rowIndex)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (blurTimer.current) window.clearTimeout(blurTimer.current);
                      selectCity(c);
                    }}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          )}

          {properties.length > 0 && (
            <div className={styles.group} role="group" aria-label="Properties">
              <div className={styles.groupLabel}>Properties</div>
              {properties.map((p, i) => {
                const rowIndex = propertyStart + i;
                const title = p.unparsedAddress || p.address || "";
                const place = [p.city, p.state].filter(Boolean).join(", ");
                const spec: string[] = [];
                if (p.beds) spec.push(p.beds + " bd");
                if (p.baths) spec.push(p.baths + " ba");
                const meta = [place, spec.join(" / ")].filter(Boolean).join(" • ");
                const price = formatPrice(p.price);
                return (
                  <button
                    key={p.id ?? `${title}-${i}`}
                    type="button"
                    role="option"
                    aria-selected={activeIndex === rowIndex}
                    className={`${styles.row} ${activeIndex === rowIndex ? styles.rowActive : ""}`}
                    onMouseEnter={() => setActiveIndex(rowIndex)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (blurTimer.current) window.clearTimeout(blurTimer.current);
                      selectProperty(p.url);
                    }}
                  >
                    <span
                      className={`${styles.thumb} ${p.image ? "" : styles.thumbBlank}`}
                      style={p.image ? { backgroundImage: `url(${p.image})` } : undefined}
                      aria-hidden="true"
                    />
                    <span className={styles.body}>
                      <span className={styles.title}>{title}</span>
                      {meta && <span className={styles.meta}>{meta}</span>}
                    </span>
                    {price && <span className={styles.price}>{price}</span>}
                  </button>
                );
              })}
            </div>
          )}

          {subdivisions.length > 0 && (
            <div className={styles.group} role="group" aria-label="Subdivisions">
              <div className={styles.groupLabel}>Subdivisions</div>
              {subdivisions.map((s, i) => {
                const rowIndex = subdivisionStart + i;
                return (
                  <button
                    key={s}
                    type="button"
                    role="option"
                    aria-selected={activeIndex === rowIndex}
                    className={`${styles.row} ${styles.tagRow} ${
                      activeIndex === rowIndex ? styles.rowActive : ""
                    }`}
                    onMouseEnter={() => setActiveIndex(rowIndex)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (blurTimer.current) window.clearTimeout(blurTimer.current);
                      selectSubdivision(s);
                    }}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          )}

          {hasQuery && (
            <button
              type="button"
              role="option"
              aria-selected={activeIndex === rows.length - 1}
              className={`${styles.row} ${styles.aiRow} ${
                activeIndex === rows.length - 1 ? styles.rowActive : ""
              }`}
              onMouseEnter={() => setActiveIndex(rows.length - 1)}
              onMouseDown={(e) => {
                e.preventDefault();
                if (blurTimer.current) window.clearTimeout(blurTimer.current);
                submit(trimmed);
              }}
            >
              <MagnifyingGlassIcon className={styles.aiRowIcon} />
              <span className={styles.body}>
                Search <strong>&ldquo;{trimmed}&rdquo;</strong>
              </span>
            </button>
          )}
        </div>
      )}

      {aiSummary && (
        <div className={styles.summary} role="status">
          <span className={styles.summaryText}>
            Interpreted as: <strong>{aiSummary}</strong>
          </span>
        </div>
      )}

      {activeFilters.length > 0 && (
        <div className={styles.activeChips} aria-label="Active filters">
          {activeFilters.map((f) => (
            <span key={f.key} className={styles.activeChip}>
              <span className={styles.activeChipLabel}>{f.label}</span>
              <button
                type="button"
                className={styles.activeChipRemove}
                onClick={f.clear}
                aria-label={`Remove filter ${f.label}`}
                title="Remove filter"
              >
                <CloseIcon />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
